import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { notification } from 'antd'

const DEFAULT_PAGE_SIZE = 500

const normalizeColumns = (columns = [], rows = []) => {
  if (columns.length) {
    return columns.map((column) =>
      typeof column === 'string' ? { key: column, header: column } : column,
    )
  }

  return Object.keys(rows[0] || {}).map((key) => ({ key, header: key }))
}

const getValueByPath = (record, path) => {
  if (!path) return ''

  return String(path)
    .split('.')
    .reduce((value, key) => (value === undefined || value === null ? value : value[key]), record)
}

const getColumnValue = (record, column) => {
  if (typeof column.value === 'function') return column.value(record)

  return getValueByPath(record, column.key)
}

const formatCsvValue = (value) => {
  if (value === undefined || value === null) return ''

  const text = value instanceof Date ? value.toISOString() : String(value)
  // Prevent values from being interpreted as formulas when the file is opened in a spreadsheet.
  const safeText = /^[=+\-@]/.test(text) ? `'${text}` : text

  return `"${safeText.replace(/"/g, '""')}"`
}

/**
 * Saves records as CSV. A column can be a key string or { key, header, value }.
 */
export const downloadCSV = ({ data = [], columns = [], fileName = 'export.csv' }) => {
  if (!data.length) {
    const error = new Error('No data found.')
    notification.error({ message: error.message })
    throw error
  }

  const normalizedColumns = normalizeColumns(columns, data)
  const rows = [
    normalizedColumns.map((column) => formatCsvValue(column.header || column.key)).join(','),
    ...data.map((record) =>
      normalizedColumns.map((column) => formatCsvValue(getColumnValue(record, column))).join(','),
    ),
  ]

  const blob = new Blob([`\uFEFF${rows.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' })
  saveAs(blob, fileName)
}

/**
 * Fetches every page using the module's active filters. By default the API receives
 * skip as an offset and limit as the end index, matching the existing list endpoints.
 */
export const fetchAllPaginatedRecords = async ({
  fetchPage,
  filters = {},
  pageSize = DEFAULT_PAGE_SIZE,
  getRecords = (response) => response?.data?.data?.employee || response?.data?.data || [],
  getTotalCount = (response) => response?.data?.data?.totalCount,
  buildPaginationParams = ({ offset, size }) => ({ skip: offset, limit: offset + size }),
}) => {
  if (typeof fetchPage !== 'function') {
    throw new Error('fetchPage is required to download all paginated records.')
  }

  const records = []
  let offset = 0
  let totalCount
  let hasMore = true

  while (hasMore) {
    // Pages must be requested in order because the next offset depends on the response.
    // eslint-disable-next-line no-await-in-loop
    const response = await fetchPage({
      ...filters,
      ...buildPaginationParams({ offset, size: pageSize }),
    })
    const pageRecords = getRecords(response) || []

    records.push(...pageRecords)
    totalCount = Number(getTotalCount(response))

    if (!Number.isFinite(totalCount)) {
      hasMore = pageRecords.length === pageSize
    } else if (records.length >= totalCount) {
      hasMore = false
    }

    if (!pageRecords.length) hasMore = false
    offset += pageRecords.length
  }

  return records
}

/**
 * Fetches all filtered records before saving a CSV, independent of table pagination.
 */
export const downloadAllPagesAsCSV = async ({ columns, fileName, ...fetchOptions }) => {
  const data = await fetchAllPaginatedRecords(fetchOptions)
  downloadCSV({ data, columns, fileName })
  return data.length
}

/**
 * Creates a reusable Excel import template. Modules own their column definitions.
 */
export const downloadExcelTemplate = async ({
  columns = [],
  fileName = 'template.xlsx',
  sheetName = 'Template',
  sampleRows = [],
}) => {
  const normalizedColumns = normalizeColumns(columns, sampleRows)
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet(sheetName)

  worksheet.columns = normalizedColumns.map((column) => ({
    header: column.header || column.key,
    key: column.key,
    width: column.width || Math.max(String(column.header || column.key).length + 2, 16),
  }))

  sampleRows.forEach((sampleRow) => {
    worksheet.addRow(
      normalizedColumns.reduce((row, column) => {
        row[column.key] = getColumnValue(sampleRow, column)
        return row
      }, {}),
    )
  })

  const header = worksheet.getRow(1)
  header.font = { bold: true, color: { argb: 'FFFFFFFF' } }
  header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1890FF' } }
  worksheet.views = [{ state: 'frozen', ySplit: 1 }]
  if (normalizedColumns.length) {
    worksheet.autoFilter = {
      from: 'A1',
      to: `${worksheet.getColumn(normalizedColumns.length).letter}1`,
    }
  }

  const buffer = await workbook.xlsx.writeBuffer()
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    fileName,
  )
}
